<template>
  <div class="min-h-screen flex flex-col">
    <AppHeader />
    
    <!-- Hero Section -->
    <section class="relative h-96 flex items-center justify-center text-white bg-gradient-stone pt-16 overflow-hidden">
      <div class="absolute inset-0">
        <img :alt="$t('resourceCenter.hero.imageAlt')" class="w-full h-full object-cover opacity-40"
             src="/images/resourceCenter/hero-bg.jpg">
      </div>
      <div class="relative z-10 text-center px-4 max-w-4xl">
        <h1 class="text-5xl md:text-6xl font-extrabold leading-tight mb-4 animate-fade-in-up">
          {{ $t('resourceCenter.hero.title') }}
        </h1>
        <p class="text-xl md:text-2xl mb-6 max-w-3xl mx-auto animate-fade-in-up delay-200">
          {{ $t('resourceCenter.hero.subtitle') }}
        </p>
        <div class="relative max-w-2xl mx-auto animate-fade-in-up delay-400">
          <input v-model="heroSearch" @keyup="handleSearch" 
                 class="py-3 pl-10 pr-4 rounded-full bg-white/90 focus:outline-none focus:ring-2 focus:ring-blue-500 w-full text-gray-800 text-lg" 
                 :placeholder="$t('resourceCenter.hero.searchPlaceholder')" type="text">
          <i class="fas fa-search absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500 text-xl"></i>
        </div>
      </div>
    </section>

    <!-- Resource Category Navigation -->
    <section class="bg-white shadow-md sticky top-16 z-40">
      <div class="max-w-7xl mx-auto px-6 md:px-0">
        <div class="flex overflow-x-auto scrollbar-hide py-4 space-x-4 md:space-x-6">
          <button v-for="tab in resourceTabs" :key="tab.value"
                  @click="activeTab = tab.value"
                  :class="[
                    'whitespace-nowrap px-8 py-2 rounded-full font-medium transition-all duration-300',
                    activeTab === tab.value 
                      ? 'bg-purple-600 text-white' 
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                  ]">
            {{ tab.label }}
          </button>
        </div>
      </div>
    </section>

    <!-- Main Content -->
    <main class="flex-grow pt-12 pb-20 px-6 md:px-12">
      <div class="max-w-7xl mx-auto">
        
        <!-- Industry Insights Section -->
        <div v-show="activeTab === 'all' || activeTab === 'insights'" class="mb-20">
          <div class="flex flex-col md:flex-row md:items-end justify-between mb-10">
            <div>
              <h2 class="text-4xl font-extrabold text-gray-900 mb-2">{{ $t('resourceCenter.insights.title') }}</h2>
              <p class="text-gray-600 max-w-3xl">{{ $t('resourceCenter.insights.subtitle') }}</p>
            </div>
            <a href="javascript:void(0);" class="mt-4 md:mt-0 text-purple-600 hover:text-purple-700 font-medium flex items-center transition duration-300">
              {{ $t('resourceCenter.insights.viewAll') }} <i class="fas fa-arrow-right ml-2"></i>
            </a>
          </div>
          
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div v-for="insight in insights" :key="insight.id"
                 class="bg-white rounded-xl shadow-lg overflow-hidden transition-all duration-300 hover:shadow-xl hover:-translate-y-1 group">
              <div class="relative h-48 overflow-hidden">
                <img :alt="insight.title" class="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" 
                     :src="insight.image">
                <div :class="['absolute top-4 left-4 text-white text-xs font-bold px-3 py-1 rounded-full', insight.badgeColor]">
                  {{ insight.badge }}
                </div>
              </div>
              <div class="p-6">
                <div class="flex items-center text-sm text-gray-500 mb-3">
                  <i class="far fa-calendar-alt mr-2"></i>
                  <span>{{ insight.date }}</span>
                  <span class="mx-2">•</span>
                  <i class="far fa-clock mr-2"></i>
                  <span>{{ insight.readTime }}</span>
                </div>
                <h3 class="text-xl font-bold mb-3 text-gray-900 group-hover:text-purple-700 transition duration-300">
                  {{ insight.title }}
                </h3>
                <p class="text-gray-600 mb-4 line-clamp-3">
                  {{ insight.description }}
                </p>
                <div class="flex items-center justify-between">
                  <div class="flex items-center">
                    <img :alt="insight.author" class="w-8 h-8 rounded-full object-cover mr-3" 
                         :src="insight.authorAvatar">
                    <span class="text-sm font-medium text-gray-700">{{ insight.author }}</span>
                  </div>
                  <a
                    :href="insight.detailUrl"
                    class="text-purple-600 hover:text-purple-700 font-medium transition duration-300"
                    target="_blank"
                    rel="noopener"
                  >
                    {{ $t('resourceCenter.insights.readMore') }} <i class="fas fa-long-arrow-alt-right ml-1"></i>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Downloads Section -->
        <div v-show="activeTab === 'all' || activeTab === 'downloads'" class="mb-20">
          <div class="flex flex-col md:flex-row md:items-end justify-between mb-10">
            <div>
              <h2 class="text-4xl font-extrabold text-gray-900 mb-2">{{ $t('resourceCenter.downloads.title') }}</h2>
              <p class="text-gray-600 max-w-3xl">{{ $t('resourceCenter.downloads.subtitle') }}</p>
            </div>
            <a href="javascript:void(0);" class="mt-4 md:mt-0 text-purple-600 hover:text-purple-700 font-medium flex items-center transition duration-300">
              {{ $t('resourceCenter.downloads.viewAll') }} <i class="fas fa-arrow-right ml-2"></i>
            </a>
          </div>
          
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div v-for="download in downloads" :key="download.id"
                 class="bg-white rounded-xl shadow-lg overflow-hidden transition-all duration-300 hover:shadow-xl hover:-translate-y-1 group border border-gray-100">
              <div class="p-6 flex flex-col h-full">
                <div class="flex items-center mb-4">
                  <div :class="['w-12 h-12 rounded-lg flex items-center justify-center mr-4 flex-shrink-0', download.iconBg]">
                    <i :class="['text-2xl', download.iconClass]"></i>
                  </div>
                  <div>
                    <div :class="['text-sm font-medium px-2 py-1 rounded', download.badgeClass]">{{ download.badge }}</div>
                    <div class="text-sm text-gray-500 mt-1">{{ download.date }}</div>
                  </div>
                </div>
                <h3 class="text-xl font-bold mb-3 text-gray-900 group-hover:text-purple-700 transition duration-300 flex-grow">
                  {{ download.title }}
                </h3>
                <p class="text-gray-600 mb-6 flex-grow">
                  {{ download.description }}
                </p>
                <div class="pt-4 border-t border-gray-100 flex items-center justify-between">
                  <div class="flex items-center text-sm text-gray-500">
                    <i class="fas fa-file-arrow-down mr-2 text-purple-500"></i>
                    <span>{{ download.fileInfo }}</span>
                  </div>
                  <a
                    :href="download.fileUrl"
                    :download="download.fileName"
                    class="bg-purple-600 hover:bg-purple-700 text-white font-medium px-4 py-2 rounded-lg transition duration-300 flex items-center"
                  >
                    <i class="fas fa-download mr-2"></i> {{ $t('resourceCenter.downloads.action') }}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Knowledge Base Section -->
        <div v-show="activeTab === 'all' || activeTab === 'knowledge'" class="mb-20">
          <div class="flex flex-col md:flex-row md:items-end justify-between mb-10">
            <div>
              <h2 class="text-4xl font-extrabold text-gray-900 mb-2">{{ $t('resourceCenter.knowledge.title') }}</h2>
              <p class="text-gray-600 max-w-3xl">{{ $t('resourceCenter.knowledge.subtitle') }}</p>
            </div>
            <div class="mt-4 md:mt-0 flex items-center space-x-4">
              <div class="relative">
                <select v-model="knowledgeCategory"
                        class="appearance-none bg-gray-100 border border-gray-200 text-gray-700 py-2 pl-4 pr-10 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition duration-300">
                  <option value="all">{{ $t('resourceCenter.knowledge.categories.all') }}</option>
                  <option value="operation">{{ $t('resourceCenter.knowledge.categories.operation') }}</option>
                  <option value="troubleshooting">{{ $t('resourceCenter.knowledge.categories.troubleshooting') }}</option>
                  <option value="compliance">{{ $t('resourceCenter.knowledge.categories.compliance') }}</option>
                  <option value="security">{{ $t('resourceCenter.knowledge.categories.security') }}</option>
                </select>
                <div class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-500">
                  <i class="fas fa-chevron-down text-xs"></i>
                </div>
              </div>
              <a href="javascript:void(0);" class="text-purple-600 hover:text-purple-700 font-medium flex items-center transition duration-300">
                {{ $t('resourceCenter.knowledge.viewAll') }} <i class="fas fa-arrow-right ml-2"></i>
              </a>
            </div>
          </div>
          
          <div class="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
            <div class="p-6 border-b border-gray-100">
              <h3 class="text-xl font-bold text-gray-900 mb-1">{{ $t('resourceCenter.knowledge.hotTitle') }}</h3>
              <p class="text-gray-600">{{ $t('resourceCenter.knowledge.hotSubtitle') }}</p>
            </div>
            <div class="divide-y divide-gray-100">
              <div v-for="doc in hotDocuments" :key="doc.id"
                   class="p-6 hover:bg-gray-50 transition duration-300 flex flex-col md:flex-row md:items-center">
                <div :class="['flex-shrink-0 w-12 h-12 rounded-lg flex items-center justify-center mb-4 md:mb-0 mr-0 md:mr-6', doc.iconBg]">
                  <i :class="['text-xl', doc.iconClass]"></i>
                </div>
                <div class="flex-grow">
                  <h4 class="text-lg font-semibold text-gray-900 mb-1 hover:text-purple-700 transition duration-300 cursor-pointer">
                    {{ doc.title }}
                  </h4>
                  <p class="text-gray-600 text-sm mb-2">{{ doc.description }}</p>
                  <div class="flex flex-wrap items-center text-xs text-gray-500">
                    <span :class="['px-2 py-1 rounded-full mr-2 mb-2', doc.categoryClass]">{{ doc.category }}</span>
                    <span class="mr-4 mb-2"><i class="far fa-eye mr-1"></i> {{ doc.views }}</span>
                    <span class="mr-4 mb-2"><i class="far fa-clock mr-1"></i> {{ doc.date }}</span>
                    <span class="mb-2"><i class="far fa-comment mr-1"></i> {{ doc.comments }}</span>
                  </div>
                </div>
                <div class="mt-4 md:mt-0 flex items-center">
                  <a
                    :href="doc.detailUrl"
                    class="text-purple-600 hover:text-purple-700 font-medium text-sm flex items-center transition duration-300"
                    target="_blank"
                    rel="noopener"
                  >
                    {{ $t('resourceCenter.knowledge.readDoc') }} <i class="fas fa-arrow-right ml-2 text-xs"></i>
                  </a>
                </div>
              </div>
            </div>
          </div>
          
          <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div class="bg-white rounded-xl shadow-lg overflow-hidden">
              <div class="p-6 bg-blue-50 border-b border-blue-100">
                <h3 class="text-lg font-bold text-blue-700 flex items-center">
                  <i class="fas fa-tools mr-3 text-blue-600"></i> {{ $t('resourceCenter.knowledge.operationTitle') }}
                </h3>
              </div>
              <div class="p-5">
                <ul class="space-y-4">
                  <li v-for="(item, index) in operationTips" :key="index">
                    <a
                      :href="item.href"
                      class="flex items-start group"
                      :download="item.download ? '' : null"
                      :target="item.download ? null : '_blank'"
                      :rel="item.download ? null : 'noopener'"
                    >
                      <i class="fas fa-angle-right text-blue-500 mt-1 mr-3 group-hover:text-blue-700 transition duration-300"></i>
                      <span class="text-gray-800 hover:text-blue-700 transition duration-300">{{ item.title }}</span>
                    </a>
                  </li>
                </ul>
              </div>
            </div>
            
            <div class="bg-white rounded-xl shadow-lg overflow-hidden">
              <div class="p-6 bg-red-50 border-b border-red-100">
                <h3 class="text-lg font-bold text-red-700 flex items-center">
                  <i class="fas fa-exclamation-triangle mr-3 text-red-600"></i> {{ $t('resourceCenter.knowledge.troubleshootingTitle') }}
                </h3>
              </div>
              <div class="p-5">
                <ul class="space-y-4">
                  <li v-for="(item, index) in troubleshootingTips" :key="index">
                    <a
                      :href="item.href"
                      class="flex items-start group"
                      :download="item.download ? '' : null"
                      :target="item.download ? null : '_blank'"
                      :rel="item.download ? null : 'noopener'"
                    >
                      <i class="fas fa-angle-right text-red-500 mt-1 mr-3 group-hover:text-red-700 transition duration-300"></i>
                      <span class="text-gray-800 hover:text-red-700 transition duration-300">{{ item.title }}</span>
                    </a>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <!-- Events Section -->
        <div v-show="activeTab === 'all' || activeTab === 'events'" class="mb-20">
          <div class="flex flex-col md:flex-row md:items-end justify-between mb-10">
            <div>
              <h2 class="text-4xl font-extrabold text-gray-900 mb-2">{{ $t('resourceCenter.events.title') }}</h2>
              <p class="text-gray-600 max-w-3xl">{{ $t('resourceCenter.events.subtitle') }}</p>
            </div>
            <a href="javascript:void(0);" class="mt-4 md:mt-0 text-purple-600 hover:text-purple-700 font-medium flex items-center transition duration-300">
              {{ $t('resourceCenter.events.viewAll') }} <i class="fas fa-arrow-right ml-2"></i>
            </a>
          </div>
          
          <div class="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <!-- Featured Event -->
            <div class="lg:col-span-2 bg-white rounded-xl shadow-lg overflow-hidden transition-all duration-300 hover:shadow-xl group">
              <div class="relative h-64 md:h-80 overflow-hidden">
                <img :alt="featuredEvent.title" class="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" 
                     src="/images/resourceCenter/event-summit.jpg">
                <div class="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent flex flex-col justify-end p-6 text-white">
                  <div class="bg-purple-600 text-white text-xs font-bold px-3 py-1 rounded-full inline-block mb-3 w-fit">
                    {{ featuredEvent.badge }}
                  </div>
                  <h3 class="text-2xl md:text-3xl font-bold mb-2">{{ featuredEvent.title }}</h3>
                  <p class="text-white/90 mb-4 max-w-2xl">{{ featuredEvent.description }}</p>
                  <div class="flex flex-wrap items-center text-white/90 text-sm">
                    <div class="flex items-center mr-6 mb-2 md:mb-0">
                      <i class="far fa-calendar-alt mr-2"></i>
                      <span>{{ featuredEvent.date }}</span>
                    </div>
                    <div class="flex items-center mr-6 mb-2 md:mb-0">
                      <i class="fas fa-map-marker-alt mr-2"></i>
                      <span>{{ featuredEvent.location }}</span>
                    </div>
                    <div class="flex items-center">
                      <i class="fas fa-user-friends mr-2"></i>
                      <span>{{ featuredEvent.audience }}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div class="p-6 flex justify-between items-center border-t border-gray-100">
                <div class="flex items-center text-sm text-gray-500">
                  <span class="bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full text-xs font-medium mr-3">
                    {{ featuredEvent.status }}
                  </span>
                  <span>{{ featuredEvent.metaNote }}</span>
                </div>
                <a
                  :href="featuredEvent.detailUrl"
                  class="bg-purple-600 hover:bg-purple-700 text-white font-medium px-5 py-2 rounded-lg transition duration-300 flex items-center"
                  target="_blank"
                  rel="noopener"
                >
                  {{ featuredEvent.action }} <i class="fas fa-arrow-right ml-2 text-sm"></i>
                </a>
              </div>
            </div>
            
            <!-- Recent Events List -->
            <div class="bg-white rounded-xl shadow-lg overflow-hidden">
              <div class="p-6 border-b border-gray-100">
                <h3 class="text-lg font-bold text-gray-900 mb-1">{{ $t('resourceCenter.events.recentTitle') }}</h3>
                <p class="text-gray-600 text-sm">{{ $t('resourceCenter.events.recentSubtitle') }}</p>
              </div>
              <div class="divide-y divide-gray-100">
                <div v-for="event in recentEvents" :key="event.id"
                     class="p-5 hover:bg-gray-50 transition duration-300 cursor-pointer">
                  <div class="flex items-start">
                    <div :class="['rounded-lg p-2 mr-4 flex flex-col items-center min-w-[60px]', event.dateBg]">
                      <span class="text-lg font-bold">{{ event.day }}</span>
                      <span class="text-xs uppercase">{{ event.month }}</span>
                    </div>
                    <div class="flex-grow">
                      <div :class="['text-xs font-bold px-2 py-0.5 rounded-full inline-block mb-2', event.badgeClass]">
                        {{ event.badge }}
                      </div>
                      <h4 class="font-semibold text-gray-900 mb-1">{{ event.title }}</h4>
                      <div class="flex items-center text-xs text-gray-500 mb-2">
                        <i :class="[event.icon, 'mr-1']"></i>
                        <span>{{ event.info }}</span>
                      </div>
                      <a
                        :href="event.detailUrl"
                        :class="['text-sm font-medium transition duration-300', event.linkClass]"
                        target="_blank"
                        rel="noopener"
                      >
                        {{ event.action }}
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </main>

    <!-- Contact CTA Section -->
    <section class="bg-gradient-stone-light text-white py-16 px-6 md:px-12">
      <div class="max-w-5xl mx-auto">
        <div class="bg-white/10 border border-white/15 rounded-3xl px-6 py-8 md:px-10 md:py-10 backdrop-blur-sm shadow-[0_24px_60px_rgba(15,23,42,0.14)]">
          <div class="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
            <div class="max-w-3xl">
              <div class="inline-flex items-center px-3 py-1 rounded-full bg-white/15 text-white/90 text-sm font-semibold mb-4">
                {{ $t('resourceCenter.contactCta.badge') }}
              </div>
              <h2 class="text-3xl md:text-4xl font-bold mb-4">{{ $t('resourceCenter.contactCta.title') }}</h2>
              <p class="text-white/90 text-base md:text-lg leading-8 mb-6">
                {{ $t('resourceCenter.contactCta.subtitle') }}
              </p>
              <div class="flex flex-wrap gap-3">
                <span
                  v-for="item in contactHighlights"
                  :key="item"
                  class="px-4 py-2 rounded-full bg-white/12 border border-white/15 text-sm text-white/90"
                >
                  {{ item }}
                </span>
              </div>
              <p class="text-white/70 text-sm mt-5">{{ $t('resourceCenter.contactCta.note') }}</p>
            </div>

            <div class="flex-shrink-0">
              <router-link
                to="/contact"
                class="inline-flex items-center justify-center bg-white text-purple-700 hover:bg-purple-50 px-7 py-4 rounded-2xl font-semibold shadow-lg transition duration-300 transform hover:scale-[1.02] whitespace-nowrap"
              >
                {{ $t('resourceCenter.contactCta.action') }}
                <i class="fas fa-arrow-right ml-3"></i>
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </section>

    <AppFooter />
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import AppHeader from '@/components/AppHeader.vue';
import AppFooter from '@/components/AppFooter.vue';
import {
  downloadMeta,
  featuredEventUrl,
  insightDetailUrls,
  knowledgeDetailUrls,
  operationTipUrls,
  recentEventUrls,
  troubleshootingTipUrls
} from '@/data/resourceCenterMeta.js';

const { t, tm } = useI18n();

// Search and filters
const heroSearch = ref('');
const activeTab = ref('all');
const knowledgeCategory = ref('all');

// Resource tabs
const resourceTabs = computed(() => [
  { value: 'all', label: t('resourceCenter.tabs.all') },
  { value: 'insights', label: t('resourceCenter.tabs.insights') },
  { value: 'downloads', label: t('resourceCenter.tabs.downloads') },
  { value: 'knowledge', label: t('resourceCenter.tabs.knowledge') },
  { value: 'events', label: t('resourceCenter.tabs.events') }
]);

// Industry insights data
const insights = computed(() => tm('resourceCenter.insights.items').map(item => ({
  ...item,
  detailUrl: insightDetailUrls[item.id] || '#'
})));

// Downloads data
const downloads = computed(() => tm('resourceCenter.downloads.items').map(item => ({
  ...item,
  ...(downloadMeta[item.id] || {})
})));

// Hot documents data
const hotDocuments = computed(() => tm('resourceCenter.knowledge.hotDocuments').map(item => ({
  ...item,
  detailUrl: knowledgeDetailUrls[item.id] || '#'
})));

// Operation tips
const operationTips = computed(() => tm('resourceCenter.knowledge.operationTips').map((title, index) => ({
  title,
  href: operationTipUrls[index] || '#',
  download: (operationTipUrls[index] || '').endsWith('.pdf')
})));

// Troubleshooting tips
const troubleshootingTips = computed(() => tm('resourceCenter.knowledge.troubleshootingTips').map((title, index) => ({
  title,
  href: troubleshootingTipUrls[index] || '#',
  download: (troubleshootingTipUrls[index] || '').endsWith('.pdf')
})));

const featuredEvent = computed(() => ({
  ...tm('resourceCenter.events.featured'),
  detailUrl: featuredEventUrl
}));

const contactHighlights = computed(() => tm('resourceCenter.contactCta.highlights'));

// Recent events data
const recentEvents = computed(() => tm('resourceCenter.events.recentItems').map(item => ({
  ...item,
  detailUrl: recentEventUrls[item.id] || '#'
})));

// Handle search
const handleSearch = () => {
  const searchTerm = heroSearch.value.toLowerCase().trim();
  if (searchTerm.length > 2) {
    console.log(`Search resources: ${searchTerm}`);
  }
};
</script>

<style>
@import '@/assets/css/main.css';
@import '@/assets/css/resourceCenter.css';
</style>
